import axios from "axios";
import { MDBDataTable } from "mdbreact";
import React, { useEffect, useMemo, useState } from "react";
import { MutatingDots } from "react-loader-spinner";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Bounce, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Button,
  Card, CardBody, CardTitle,
  Col,
  Modal,
  ModalBody, ModalFooter,
  ModalHeader,
  Row
} from "reactstrap";
import api from "../../services/api";
import { setBreadcrumbItems } from "../../store/actions";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

const CourseDiscountsList = (props) => {
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [discounts, setDiscounts] = useState([]);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(true);

  const breadcrumbItems = [
    { title: "Technical Hub", link: "#" },
    { title: "Course Discounts", link: "#" },
    { title: "Course Discounts List", link: "#" },
  ];

  useEffect(() => {
    const getDiscounts = async () => {
      try {
        const response = await api.get(`${backendUrl}get-all-discounts`);
        if (response.status === 200) {
          console.log("Fetched Discounts:", response.data.result);
          setDiscounts(response.data.result);
        }
      } catch (err) {
        toast.error("Failed to fetch discounts");
      } finally {
        setLoading(false);
      }
    };
    getDiscounts();
    props.setBreadcrumbItems("Course Discounts", breadcrumbItems);
  }, []);

  const toggleModal = () => setModalOpen(!modalOpen);
  const toggleDeleteModal = () => setDeleteModalOpen(!deleteModalOpen);

  const handleOpenList = (discount) => {
    console.log("Selected Discount:", discount);
    setSelectedDiscount(discount);
    setModalOpen(true);
  };

  const handleDeleteTerm = async (term, id) => {
    try {
      const response = await axios.delete(`${backendUrl}delete-term-discount/${id}/${term.term_name}`);
      if (response.status === 200) {
        toast.success(response.data.message);

        const { deleted } = response.data;

        // If backend deleted the entire discount record
        if (response.data.message.includes("Entire discount record removed")) {
          // Remove from main list
          setDiscounts((prev) => prev.filter((d) => d._id !== id));
          setSelectedDiscount(null); // Close modal
          setModalOpen(false);
        } else {
          // Only remove one term
          setSelectedDiscount((prev) => ({
            ...prev,
            discount_details: prev.discount_details.filter(
              (t) => t.term_name !== deleted
            ),
          }));

          // Also update main list for consistency
          setDiscounts((prev) =>
            prev.map((d) =>
              d._id === id
                ? {
                  ...d,
                  discount_details: d.discount_details.filter(
                    (t) => t.term_name !== deleted
                  ),
                }
                : d
            )
          );
        }
      } else {
        toast.error("Failed to delete term discount");
      }
    } catch (err) {
      toast.error("Error deleting term discount");
    }
  };


  // const handleDeleteClick = (term) => {
  //   setSelectedTerm(term);
  //   setDeleteModalOpen(true);
  // };

  const handleConfirmDelete = async () => {
    if (!selectedDiscount || !selectedTerm) return;

    setIsDeleting(true);
    try {
      const payload = {
        roll_no: selectedDiscount.roll_no,
        reg_id: selectedDiscount.reg_id,
        course_id: selectedDiscount.course_id,
        technology_id: selectedDiscount.technology_id,
        term_name: selectedTerm.term_name
      };

      const response = await axios.delete(`${backendUrl}delete-discount`, { data: payload });
      if (response.status === 200) {
        toast.success("Discount deleted successfully");
        // Update term details locally
        setSelectedDiscount((prev) => ({
          ...prev,
          discount_details: prev.discount_details.filter(
            (term) => term.term_name !== selectedTerm.term_name
          ),
        }));
        // Update main list
        setDiscounts((prev) =>
          prev.map((d) =>
            d.roll_no === selectedDiscount.roll_no ? { ...selectedDiscount } : d
          )
        );
      } else {
        toast.error("Failed to delete discount");
      }
    } catch (err) {
      toast.error("Error deleting discount");
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
    }
  };


  const handleEditTerm = (term) => {
    navigate(
      `/edit-course-discount/${selectedDiscount._id}/${term.term_name}`
    );
  };

  const data = useMemo(
    () => ({
      columns: [
        { label: "Roll No", field: "roll_no", sort: "asc", width: 50 },
        { label: "Name", field: "first_name", sort: "asc", width: 100 },
        { label: "Course", field: "course_name", sort: "asc", width: 100 },
        { label: "Program", field: "program_name", sort: "asc", width: 150 },
        { label: "Technology", field: "technology_name", sort: "asc", width: 120 },
        { label: "Discount Type", field: "discount_type", sort: "asc", width: 100 },
        // { label: "Remarks", field: "remarks", sort: "asc", width: 100 },
        { label: "Actions", field: "actions", sort: "asc", width: 100 },
      ],
      rows: discounts.map((discount) => ({
        roll_no: discount.roll_no,
        course_name: discount.course_name,
        first_name: discount.first_name,
        program_name: discount.program_name,
        technology_name: discount.technology_name,
        discount_type: discount.discount_type,
        // remarks: discount.remarks,
        actions: (
          <Button
            color="primary"
            size="sm"
            onClick={() => handleOpenList(discount)}
          >
            View Term Details
          </Button>
        ),
      })),
    }),
    [discounts]
  );

  return (
    <React.Fragment>
      <Row>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          transition={Bounce}
        />
        <Col className="col-12">
          <Card>
            <CardBody className="d-flex justify-content-between align-items-center">
              <CardTitle className="h4">Course Discounts List</CardTitle>
              <Button color="primary" tag="a" href="/add-course-discount">
                Add Discount
              </Button>
            </CardBody>
            <CardBody>
              {loading ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "300px" }}
                >
                  <MutatingDots
                    visible
                    height="100"
                    width="100"
                    color="#2B3A4A"
                    secondaryColor="#2B3A4A"
                    radius="12.5"
                    ariaLabel="mutating-dots-loading"
                  />
                </div>
              ) : (
                <MDBDataTable responsive bordered data={data} />
              )}

            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Modal for Term Details */}
      <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>
          Term Details - {selectedDiscount?.first_name} ({selectedDiscount?.roll_no})
        </ModalHeader>
        <ModalBody>
          {selectedDiscount?.discount_details?.length ? (
            <table className="table table-bordered text-center">
              <thead>
                <tr>
                  <th>Term Name</th>
                  <th>Term Amount</th>
                  <th>Discount Amount</th>
                  <th>Balance</th>
                  <th>Remarks</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedDiscount.discount_details.map((term, index) => (
                  <tr key={index}>
                    <td>{term.term_name}</td>
                    <td>{term.term_amount}</td>
                    <td>{term.amount}</td>
                    <td>{term.balance}</td>
                    <td>{term.remarks}</td>
                    <td>
                      <Button
                        size="sm"
                        color="primary"
                        className="me-2"
                        onClick={() => handleEditTerm(term)}
                      >
                        Edit
                      </Button>
                      {/* <Button
                        size="sm"
                        color="danger"
                        onClick={() => handleDeleteTerm(term, selectedDiscount._id)}
                      >
                        Delete
                      </Button> */}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No term discount details found.</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        toggle={toggleDeleteModal}
        onDelete={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </React.Fragment>
  );
};

export default connect(null, { setBreadcrumbItems })(CourseDiscountsList);
